/**
 * LocalityDialog — cascading dropdowns for setting the user's active locality.
 *
 * Flow:
 *   1. Choose Country from a searchable dropdown (~250 countries)
 *   2. Choose State/Province from a dropdown populated for that country
 *      (ISO 3166-2 subdivisions via /api/geo/subdivisions)
 *   3. Choose City/Town/Village via live autocomplete (Nominatim-backed)
 *   4. Optional postal code
 *   5. "Go" → save + navigate to the deepest level article page
 *      (country-only → /l/country ; +state → /l/country/state ;
 *       +city → /l/country/state/city)
 *
 * A secondary "Detect automatically" button uses browser geolocation as a
 * shortcut. On failure (permission denied, timeout, or the geocode call
 * throwing "Network Error"), it silently falls back to server-side
 * IP-based lookup so the user always gets *some* result rather than a
 * scary error toast.
 *
 * Accessibility: labels, inputs, and buttons use the site's `text-base`
 * size and the darker `--wofy-text` colour by default — chosen for
 * senior/low-vision users who couldn't read the previous tiny grey labels.
 *
 * Persistence: signed-in users save to `/api/locality/me`; guests save to
 * localStorage. Manually-set localities are tagged `source: "manual"` so the
 * top "You're viewing from..." banner can suppress itself.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Locate, X, Search, ChevronDown, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import { useLocality } from "@/contexts/LocalityContext";
import CityAutocomplete from "@/components/CityAutocomplete";

// Turn "United States" / "New York" / "New York City" into a URL slug segment.
const slugSegment = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Last-ditch fallback: guess the user's country from the browser IANA
// timezone. Covers the vast majority of real-world sign-ups so we can
// still pre-fill the country dropdown even when both browser geolocation
// AND the IP-lookup providers are unavailable. Values are ISO-3166-1
// alpha-2 codes matching /api/geo/countries.
const TZ_TO_COUNTRY = {
  // Americas
  "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US",
  "America/Los_Angeles": "US", "America/Phoenix": "US", "America/Anchorage": "US",
  "America/Detroit": "US", "America/Indiana/Indianapolis": "US",
  "America/Toronto": "CA", "America/Vancouver": "CA", "America/Edmonton": "CA",
  "America/Winnipeg": "CA", "America/Halifax": "CA", "America/St_Johns": "CA",
  "America/Mexico_City": "MX", "America/Monterrey": "MX", "America/Tijuana": "MX",
  "America/Sao_Paulo": "BR", "America/Bahia": "BR", "America/Fortaleza": "BR",
  "America/Argentina/Buenos_Aires": "AR",
  "America/Santiago": "CL", "America/Lima": "PE", "America/Bogota": "CO",
  "America/Caracas": "VE", "America/La_Paz": "BO",
  // Europe
  "Europe/London": "GB", "Europe/Dublin": "IE",
  "Europe/Paris": "FR", "Europe/Berlin": "DE", "Europe/Madrid": "ES",
  "Europe/Rome": "IT", "Europe/Amsterdam": "NL", "Europe/Brussels": "BE",
  "Europe/Vienna": "AT", "Europe/Zurich": "CH", "Europe/Stockholm": "SE",
  "Europe/Oslo": "NO", "Europe/Copenhagen": "DK", "Europe/Helsinki": "FI",
  "Europe/Warsaw": "PL", "Europe/Prague": "CZ", "Europe/Budapest": "HU",
  "Europe/Athens": "GR", "Europe/Istanbul": "TR", "Europe/Moscow": "RU",
  "Europe/Kiev": "UA", "Europe/Kyiv": "UA", "Europe/Lisbon": "PT",
  // Asia
  "Asia/Kolkata": "IN", "Asia/Calcutta": "IN",
  "Asia/Karachi": "PK", "Asia/Dhaka": "BD", "Asia/Colombo": "LK", "Asia/Kathmandu": "NP",
  "Asia/Dubai": "AE", "Asia/Riyadh": "SA", "Asia/Qatar": "QA", "Asia/Kuwait": "KW",
  "Asia/Jerusalem": "IL", "Asia/Beirut": "LB", "Asia/Tehran": "IR",
  "Asia/Baghdad": "IQ", "Asia/Amman": "JO",
  "Asia/Shanghai": "CN", "Asia/Hong_Kong": "HK", "Asia/Taipei": "TW",
  "Asia/Tokyo": "JP", "Asia/Seoul": "KR", "Asia/Singapore": "SG",
  "Asia/Bangkok": "TH", "Asia/Jakarta": "ID", "Asia/Manila": "PH",
  "Asia/Kuala_Lumpur": "MY", "Asia/Ho_Chi_Minh": "VN", "Asia/Yangon": "MM",
  // Africa
  "Africa/Cairo": "EG", "Africa/Nairobi": "KE", "Africa/Johannesburg": "ZA",
  "Africa/Lagos": "NG", "Africa/Casablanca": "MA", "Africa/Algiers": "DZ",
  "Africa/Accra": "GH", "Africa/Addis_Ababa": "ET", "Africa/Dar_es_Salaam": "TZ",
  // Oceania
  "Australia/Sydney": "AU", "Australia/Melbourne": "AU", "Australia/Brisbane": "AU",
  "Australia/Perth": "AU", "Australia/Adelaide": "AU",
  "Pacific/Auckland": "NZ", "Pacific/Honolulu": "US", "Pacific/Fiji": "FJ",
};

const guessCountryFromTimezone = (tz) => {
  if (!tz) return null;
  if (TZ_TO_COUNTRY[tz]) return TZ_TO_COUNTRY[tz];
  // Prefix fallback: "America/Argentina/Salta" → try "America/Argentina/Buenos_Aires".
  for (const [known, cc] of Object.entries(TZ_TO_COUNTRY)) {
    const knownRoot = known.split("/").slice(0, 2).join("/");
    const tzRoot = tz.split("/").slice(0, 2).join("/");
    if (knownRoot === tzRoot) return cc;
  }
  return null;
};

export default function LocalityDialog({ onClose }) {
  const navigate = useNavigate();
  const { locality, save } = useLocality();
  const [countries, setCountries] = useState([]);
  const [subs, setSubs] = useState([]);
  const [countryCode, setCountryCode] = useState("");
  const [countryName, setCountryName] = useState(locality?.country || "");
  const [stateName, setStateName] = useState(locality?.state || "");
  const [city, setCity] = useState(locality?.city || "");
  const [postal, setPostal] = useState(locality?.postal_code || "");
  const [busy, setBusy] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [countryFilter, setCountryFilter] = useState("");
  const [countryOpen, setCountryOpen] = useState(false);

  // Load countries once.
  useEffect(() => {
    http.get("/geo/countries").then((r) => {
      setCountries(r.data.countries || []);
      // Pre-seed country code from existing locality name.
      if (locality?.country) {
        const match = (r.data.countries || []).find(
          (c) => c.name.toLowerCase() === locality.country.toLowerCase(),
        );
        if (match) setCountryCode(match.code);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load subdivisions when country changes.
  useEffect(() => {
    if (!countryCode) { setSubs([]); return; }
    http.get(`/geo/subdivisions?country=${countryCode}`)
      .then((r) => setSubs(r.data.subdivisions || []))
      .catch(() => setSubs([]));
  }, [countryCode]);

  const filteredCountries = useMemo(() => {
    const q = countryFilter.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter((c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
  }, [countries, countryFilter]);

  const selectCountry = (c) => {
    setCountryCode(c.code);
    setCountryName(c.name);
    // When the country changes, wipe both chained fields so users
    // don't accidentally submit "Portland, Oregon, France".
    setStateName("");
    setCity("");
    setCountryOpen(false);
    setCountryFilter("");
  };

  // Wipe city (but not state) when the state changes — same guardrail.
  const selectState = (name) => {
    setStateName(name);
    setCity("");
  };

  // Two-stage detect: browser geolocation → reverse-geocode; if either
  // fails, fall back to IP-based country lookup so the user always gets
  // *some* result instead of a scary "Network Error" toast.
  const detect = async () => {
    setDetecting(true);
    let filled = false;
    let geoDenied = false;
    try {
      if (!("geolocation" in navigator)) throw new Error("no-geolocation");
      const coords = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          (err) => reject(err),
          { timeout: 8000, maximumAge: 300_000 },
        );
      });
      const { data } = await http.get(`/geocode/reverse?lat=${coords.lat}&lng=${coords.lng}`);
      if (data.country) {
        const match = countries.find((c) => c.name.toLowerCase() === data.country.toLowerCase());
        if (match) { setCountryCode(match.code); setCountryName(match.name); }
        else setCountryName(data.country);
      }
      setStateName(data.state || "");
      setCity(data.city || "");
      setPostal(data.postal_code || "");
      filled = !!(data.country || data.city);
      if (filled) toast.success("Location detected — review the fields and click Go.");
    } catch (e) {
      // Detect if user explicitly denied browser geolocation permission so
      // we can give a targeted hint later.
      geoDenied = e && (e.code === 1 || String(e.message || "").toLowerCase().includes("denied"));
      console.warn("[LocalityDialog] navigator/reverse geocode failed:", e?.message || e);
    }

    if (!filled) {
      try {
        const { data } = await http.get("/geo/lookup");
        if (data?.country_code) {
          const match = countries.find((c) => c.code === data.country_code);
          if (match) {
            setCountryCode(match.code);
            setCountryName(match.name);
            setStateName("");
            setCity("");
            filled = true;
            toast.info(
              `Detected your country as ${match.name}. Please pick a state and city.`,
            );
          }
        }
      } catch { /* non-fatal */ }
    }

    // Last-ditch: guess the country from the browser's IANA timezone.
    // "Asia/Kolkata" → India, "America/New_York" → United States. This
    // costs nothing and rescues the flow when both browser geolocation
    // AND the IP providers are unavailable (rate-limited on production
    // egress, corporate proxy, VPN, etc.).
    if (!filled) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        const guess = guessCountryFromTimezone(tz);
        if (guess) {
          const match = countries.find((c) => c.code === guess);
          if (match) {
            setCountryCode(match.code);
            setCountryName(match.name);
            filled = true;
            toast.info(
              `Guessed your country as ${match.name} from your device timezone. Please pick a state and city, then Go.`,
            );
          }
        }
      } catch { /* non-fatal */ }
    }

    if (!filled) {
      toast.error(
        geoDenied
          ? "Location permission was blocked in your browser. Please allow location access for this site, or pick your country manually below."
          : "Couldn't detect your location automatically. Please pick your country manually below.",
      );
    }
    setDetecting(false);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!countryName) { toast.error("Please choose a country."); return; }
    // State + city are optional — user can save with just a country
    // and drill down later. That's the whole point of the cascading Go button.

    setBusy(true);
    try {
      // Best-effort forward-geocode so weather / community-hub have coords.
      let lat = null, lng = null;
      try {
        const q = [city.trim(), stateName, countryName].filter(Boolean).join(", ");
        if (q) {
          const { data } = await http.get(`/geocode/forward?q=${encodeURIComponent(q)}`);
          lat = data.lat; lng = data.lng;
        }
      } catch { /* non-fatal */ }

      await save({
        country: countryName,
        country_code: countryCode || null,
        state: stateName || null,
        city: city.trim() || null,
        postal_code: postal.trim() || null,
        lat, lng,
        source: "manual",
      });

      // Build the deepest-available slug and navigate.
      const parts = [countryName, stateName, city.trim()].filter(Boolean).map(slugSegment).filter(Boolean);
      const slug = parts.join("/");

      // Fire-and-forget: kick off the AI encyclopedia article for the
      // deepest level the user picked — usually the article is already
      // ready by the time the router lands on it.
      if (slug) {
        http.post("/locations/kick", { slug }).catch(() => null);
      }

      toast.success("Taking you to your location page…");
      onClose?.();
      if (slug) navigate(`/l/${slug}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save location.");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="locality-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.35)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6"
        style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)", color: "var(--wofy-text)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">YOUR LOCATION</div>
            <h2 className="font-display text-2xl mt-1">Set as my location</h2>
          </div>
          <button onClick={onClose} className="wofy-btn-ghost p-1" data-testid="locality-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <button
          type="button"
          data-testid="locality-detect-btn"
          onClick={detect}
          disabled={detecting}
          className="w-full mb-4 inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-base font-medium hover:bg-black/[0.03]"
          style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
        >
          {detecting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Locate className="w-5 h-5" />}
          {detecting ? "Detecting…" : "Detect automatically"}
        </button>

        <div className="my-3 flex items-center gap-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          <div className="flex-1 h-px" style={{ background: "var(--wofy-border)" }} />
          <span className="font-mono uppercase tracking-widest">or choose</span>
          <div className="flex-1 h-px" style={{ background: "var(--wofy-border)" }} />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {/* Country dropdown */}
          <div className="relative">
            <label className="text-sm font-semibold" style={{ color: "var(--wofy-text)" }}>
              Country
            </label>
            <button
              type="button"
              data-testid="locality-country-toggle"
              onClick={() => setCountryOpen((o) => !o)}
              className="mt-1 w-full rounded-lg border px-4 py-3 text-base text-left flex items-center justify-between font-medium"
              style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
            >
              <span className="truncate">
                {countryName
                  ? <>{countries.find((c) => c.code === countryCode)?.flag} {countryName}</>
                  : <span style={{ color: "var(--wofy-text-soft)" }}>Choose a country…</span>}
              </span>
              <ChevronDown className="w-5 h-5 opacity-70" />
            </button>
            {countryOpen && (
              <div
                data-testid="locality-country-list"
                className="absolute z-10 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border shadow-lg"
                style={{ background: "var(--wofy-bg)", borderColor: "var(--wofy-border)" }}
              >
                <div className="p-2 border-b sticky top-0" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}>
                  <div className="flex items-center gap-2 rounded border px-3 py-2" style={{ borderColor: "var(--wofy-border)" }}>
                    <Search className="w-4 h-4 opacity-60" />
                    <input
                      autoFocus
                      value={countryFilter}
                      onChange={(e) => setCountryFilter(e.target.value)}
                      placeholder="Search countries…"
                      className="flex-1 outline-none bg-transparent text-base"
                      style={{ color: "var(--wofy-text)" }}
                      data-testid="locality-country-search"
                    />
                  </div>
                </div>
                {filteredCountries.length === 0 && (
                  <p className="p-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>No countries match.</p>
                )}
                {filteredCountries.map((c) => (
                  <button
                    type="button"
                    key={c.code}
                    data-testid={`locality-country-opt-${c.code}`}
                    onClick={() => selectCountry(c)}
                    className="w-full text-left px-4 py-3 text-base hover:bg-black/[0.05] flex items-center gap-2"
                    style={{ color: "var(--wofy-text)" }}
                  >
                    <span>{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="text-xs font-mono opacity-60">{c.code}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* State/Province */}
          <label className="block">
            <span className="text-sm font-semibold" style={{ color: "var(--wofy-text)" }}>
              State / Province
            </span>
            {subs.length > 0 ? (
              <select
                data-testid="locality-state-select"
                value={stateName}
                onChange={(e) => selectState(e.target.value)}
                className="mt-1 w-full rounded-lg border px-4 py-3 text-base font-medium bg-white"
                style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
                disabled={!countryCode}
              >
                <option value="">{countryCode ? "Select a state / province…" : "Choose a country first"}</option>
                {subs.map((s) => (
                  <option key={s.code} value={s.name}>{s.name}</option>
                ))}
              </select>
            ) : (
              <input
                data-testid="locality-state-input"
                value={stateName}
                onChange={(e) => selectState(e.target.value)}
                placeholder={countryCode ? "Type a state / province…" : "Choose a country first"}
                disabled={!countryCode}
                className="mt-1 w-full rounded-lg border px-4 py-3 text-base font-medium disabled:opacity-60"
                style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
              />
            )}
          </label>

          {/* City / Town / Village — live autocomplete acts as a searchable dropdown */}
          <CityAutocomplete
            countryCode={countryCode}
            value={city}
            onSelect={(picked) => {
              setCity(picked.city || "");
              if (picked.state && !stateName) setStateName(picked.state);
            }}
            onChange={setCity}
          />

          {/* Postal (optional) */}
          <label className="block">
            <span className="text-sm font-semibold" style={{ color: "var(--wofy-text)" }}>
              Postal / Zip code <span className="font-normal opacity-60">(optional)</span>
            </span>
            <input
              data-testid="locality-postal-input"
              value={postal}
              onChange={(e) => setPostal(e.target.value)}
              className="mt-1 w-full rounded-lg border px-4 py-3 text-base font-medium"
              style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
            />
          </label>

          <button
            type="submit"
            data-testid="locality-go-btn"
            disabled={busy || !countryName}
            className="w-full wofy-btn-primary justify-center text-base font-semibold py-3 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
            {busy ? "Loading…" : "Go"}
          </button>

          <p className="text-sm text-center" style={{ color: "var(--wofy-text-soft)" }}>
            Pick just a country to see the country page, add a state for the state page, or all three for the city page.
          </p>
        </form>
      </div>
    </div>
  );
}
